import { observer } from 'mobx-react'
import { PostTypes } from '~/@types/post'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import { useDebounce } from '~/hooks/useDebounce'
import {
  AiArtworkSameType,
  AiArtworkServiceTypeGraphQLEnum,
  VisibleType,
  aiArtworkPostSame,
  aiArtworkPostSameVariables,
} from '~/graphqls/types'
import { useMutation } from '@apollo/client'
import { AI_ARTWORK_POST_SAME } from '~/graphqls/gqls'
import { useValidateIntergal } from '~/hooks/useValidateIntergal'
import { Modal, message } from 'antd'
import { graphQLErrorCode, graphQLErrorMessage } from '~/utils'
import classNames from 'classnames'
import styles from './AgainButton.module.css'
import { MyIcon } from '~/components/ui/MyIcon'
import toast from 'react-hot-toast'
import Link from 'next/link'
import { pendingAtom } from './updateAtom'
import { useAtom } from 'jotai'
import { usePostFollow } from '~/hooks/usePostFollow'
import { useState } from 'react'

function AgainButton({ post, isFocus }: { post: PostTypes; isFocus: boolean }) {
  const { validateIsLogin } = useValidateAuth()
  const [createPostAction] = useMutation<
    aiArtworkPostSame,
    aiArtworkPostSameVariables
  >(AI_ARTWORK_POST_SAME)
  const { IsNotEnough, intergalModal } = useValidateIntergal()
  const [pending, setPending] = useAtom(pendingAtom)
  const { handleClick } = usePostFollow({
    postUserId: post?.user?.uid,
    status: post?.user?.social?.followStatus,
  })

  const [isButtonFocus, setIsButtonFocus] = useState(false)

  const isLoading = !!pending.find(item => item.postID === post.id)?.postID
  // 本地的一键同款数据
  const [localCount, setLocalCount] = useState(0)
  // 计算后的一键同款数据
  const sameCount = (post.sameCount || 0) + localCount

  const onVote = () => {
    if (validateIsLogin()) {
      window.open(`/ai-competition/product/${post?.artwork?.id}`, '_blank')
    }
  }

  const handleSameAgain = useDebounce(async () => {
    if (!validateIsLogin()) return

    try {
      const res = await createPostAction({
        variables: {
          param: {
            postId: post.id,
            sameType: AiArtworkSameType.CREATE_LIKEWISE,
          },
        },
      })
      setLocalCount(prev => prev + 1)
      // console.log(res)
      // success callback
      //专业版同款去专业版
      if (
        post.imageList?.[0].aiArtwork?.serviceType ===
        AiArtworkServiceTypeGraphQLEnum.PROFESSIONAL
      ) {
        window.open(`${process.env.NEXT_PUBLIC_WUJIEAI_PRO_HOST}/ai/task`)
        return
      }
      const toastId = toast.loading(
        t => {
          return (
            <div className="flex items-center">
              <div>一键同款中，</div>
              <Link
                href={`/ai/${res.data?.squareModule?.aiArtworkId}`}
                className="text-primary"
              >
                查看详情
              </Link>
              <div className="ml-2">
                <MyIcon
                  icon="close"
                  className="text-neutral-400 text-[16px] cursor-pointer"
                  onClick={() => toast.dismiss(t.id)}
                />
              </div>
            </div>
          )
        },
        {
          iconTheme: {
            primary: 'rgba(53, 57, 69, 1)',
            secondary: 'rgba(120, 81, 236, 1)',
          },
        }
      )
      setPending(prev => {
        return [
          ...prev,
          {
            toastID: toastId,
            productID: res.data?.squareModule?.aiArtworkId,
            postID: post.id!,
          },
        ]
      })
      setIsButtonFocus(false)
    } catch (err) {
      if (IsNotEnough(err)) {
        intergalModal()
        return
      }
      if (graphQLErrorCode(err) === 20061111) {
        if (post.visibleType === VisibleType.ONLY_FANS) {
          // 关注一键同款
          Modal.confirm({
            title: '提示',
            maskStyle: {
              backgroundColor: 'rgba(0,0,0,0.8)',
            },
            width: 270,
            className: `hookModal dark`,
            content: (
              <div className="text-neutral-400 text-sm text-center">
                您需关注才能一键同款
              </div>
            ),
            okText: '关注',
            onOk: close => {
              handleClick({
                isCurrentAttention: 'NO',
              })
              close()
            },
            onCancel: close => {
              close()
            },
          })
          return
        } else if (post.visibleType === VisibleType.ONLY_VOTED) {
          // 投票可一键同款
          Modal.confirm({
            title: '提示',
            maskStyle: {
              backgroundColor: 'rgba(0,0,0,0.8)',
            },
            width: 270,
            className: `hookModal dark`,
            content: (
              <div className="text-neutral-400 text-sm text-center">
                您需去投票才能一键同款
              </div>
            ),
            okText: '去投票',
            onOk: close => {
              onVote()
              close()
            },
            onCancel: close => {
              close()
            },
          })
          return
        }
        return
      }

      // defualt behavior
      message.destroy()
      message.error(graphQLErrorMessage(err) || '一键同款操作失败，请稍后再试~')
    }
  }, [post])

  return (
    <div
      className={classNames(
        '!rounded-full !py-0.5 !px-1 cursor-pointer flex justify-end flex-shrink-0 overflow-hidden whitespace-nowrap',
        styles.btn,
        {
          [styles.active]: post.sameCreated || !!localCount,
          [styles.loading]: isLoading,
          [styles.focus]: isFocus,
        }
      )}
      onMouseEnter={() => setIsButtonFocus(true)}
      onMouseLeave={() => setIsButtonFocus(false)}
    >
      {isLoading && (
        <div className="w-3 h-3 rounded-full border-2 border-solid border-primary border-r-neutral-300 animate-spin"></div>
      )}
      {!isLoading && (
        <div
          className="w-full h-full relative flex justify-center items-center text-neutral-400 text-[12px]"
          onClick={() => {
            handleSameAgain()
          }}
        >
          {
            <>
              <MyIcon
                icon="hand-pointer"
                className={classNames('text-[12px]', styles.icon)}
              />
              {isButtonFocus ? (
                <span
                  className={classNames(
                    'text-[12px] leading-4 text-neutral-800 pl-1'
                  )}
                >
                  一键同款
                </span>
              ) : (
                <span
                  className={classNames(
                    'leading-none px-1 flex justify-center items-center h-[16px] rounded-2xl',
                    styles.count
                  )}
                >
                  {sameCount > 999 ? '999+' : sameCount}
                </span>
              )}
            </>
          }
        </div>
      )}
    </div>
  )
}

export default observer(AgainButton)
